import React, { useState } from "react";
import styled from "styled-components";
import {
  Typography,
  Box,
  Divider,
  PriceInput,
  Button,
  Icon,
} from "@bca/ui-library";

import { formatMoney } from "./formatMoney";

export interface BidNowContentProps {
  bidAmount: number;
  reserveMet: boolean;
  increment: number;
  currentBid: number;
  bidHistory: any;
  startingBidAmount: number;
  userBidStatus: string;
  myMaxProxy: number;
  nextPossibleBid: number;
  openBidNowModal: () => void;
  setBidAmount: (value: number) => void;
  getBiddingInfo: () => void;
  setInfo: (value: any) => void;
}

const ReserveMet = styled(Box)`
  border-radius: 4px;
`;

const ReserveNotMet = styled(Box)`
  border: 1px solid #64798c;
  border-radius: 4px;
`;

const BidNowContent: React.FunctionComponent<BidNowContentProps> = ({
  bidAmount = 0,
  reserveMet,
  increment,
  currentBid,
  bidHistory,
  startingBidAmount,
  userBidStatus,
  myMaxProxy,
  nextPossibleBid,
  openBidNowModal,
  setBidAmount,
  getBiddingInfo,
  setInfo,
}) => {
  const [errorMessage, setErrorMessage] = useState(null);
  const numberOfBids =
    bidHistory.length === 1 ? "1 bid" : `${bidHistory.length} bids`;
  const proxyBidSet = myMaxProxy > 0;

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {userBidStatus === "WithYou" && (
          <Typography mt={0} mb={1} variant="h6" color="secondary-green">
            You are the highest bidder
          </Typography>
        )}
        {userBidStatus === "AgainstYou" && (
          <Typography mt={0} mb={1} variant="h6" color="primary-red">
            You have been outbid, bid again now
          </Typography>
        )}
        {userBidStatus === "NoBids" && (
          <Typography variant="h6" mt={0} mb={1}>
            {bidHistory.length === 0 ? "Starting Bid" : "Current Bid"}
          </Typography>
        )}
        <Button
          variant="tertiary"
          px={0}
          height={22}
          onClick={() => {
            // we set info to null in order to display the skeleton while getBiddingInfo call is in progress
            // to make sure we have a visual feedback when pressing refresh button
            setInfo(null);
            getBiddingInfo();
          }}
          data-label="bidnow"
          data-action="refresh"
        >
          <Icon name="redo-alt" width={12} height={12} weight="solid" mr={1} />
          Refresh
        </Button>
      </Box>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography
          css={`
            font-size: 30px;
            font-weight: 500;
          `}
          m={0}
          mr={4}
        >
          {formatMoney(
            bidHistory.length === 0 ? startingBidAmount : currentBid
          )}
        </Typography>
        {reserveMet && (
          <ReserveMet
            backgroundColor="grey-blue"
            height={26}
            py={0}
            px={"10px"}
          >
            <Typography
              variant="caption"
              color="primary-white"
              m={0}
              css={`
                font-size: 13px;
                line-height: 24px;
              `}
            >
              Reserve met
            </Typography>
          </ReserveMet>
        )}
        {!reserveMet && (
          <ReserveNotMet backgroundColor="white" height={26} py={0} px={"10px"}>
            <Typography
              variant="caption"
              color="grey-blue"
              m={0}
              css={`
                font-size: 13px;
                line-height: 24px;
              `}
            >
              Reserve not met
            </Typography>
          </ReserveNotMet>
        )}
      </Box>
      <Box display="flex" mb={3}>
        <Typography m={0} variant="body" color="grey-blue">
          {numberOfBids}
        </Typography>
        <Divider
          orientation="vertical"
          width="1px"
          marginTop={0}
          marginBottom={0}
          marginLeft={3}
          marginRight={3}
        />
        {proxyBidSet && (
          <Typography
            m={0}
            variant="body"
            color="grey-blue"
            css={`
              text-decoration: ${userBidStatus === "AgainstYou"
                ? "line-through"
                : "none"};
            `}
          >
            {`Your current Max Bid is ${formatMoney(myMaxProxy)}`}
          </Typography>
        )}
        {!proxyBidSet && (
          <Typography m={0} variant="body" color="grey-blue">
            {`£${increment} Increments`}
          </Typography>
        )}
      </Box>
      {proxyBidSet && (
        <Typography m={0} mb={3} variant="body" color="grey-blue">
          {`£${increment} Increments`}
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection={["column", "row"]}
        justifyContent="space-between"
      >
        <Box width={["auto", "50%", 200]} mb={[3, 0]}>
          <PriceInput
            onChange={(newValue) => {
              setBidAmount(newValue);
              setErrorMessage(null);
            }}
            value={bidAmount || 0}
            step={increment}
            decrementDisabled={bidAmount <= nextPossibleBid}
            invalidInput={!!errorMessage}
            css={`
              width: 100%;
            `}
          />
        </Box>
        <Button
          width={["auto", 150, 150]}
          variant="primary-gradient"
          ml={[0, 0, 4]}
          p={24}
          onClick={() => {
            if (bidAmount % increment !== 0) {
              setErrorMessage("wrongIncrement");
              return;
            }

            if (bidAmount < nextPossibleBid) {
              setErrorMessage("amountTooLow");
              return;
            }
            openBidNowModal();
          }}
          disabled={!!errorMessage}
          data-en="click"
          data-ec="button"
          data-el="bidnow"
          data-ea="open"
          data-es=""
        >
          Set Max Bid
        </Button>
      </Box>
      {errorMessage && (
        <>
          {errorMessage === "wrongIncrement" && (
            <Typography m={0} variant="body" color="secondary-orange">
              {`Bids can only be placed in increments of £${increment}.`}
            </Typography>
          )}
          <Box display="flex">
            <Typography m={0} mr={1} variant="body" color="secondary-orange">
              {`The next valid bid is `}
            </Typography>
            <Button
              m={0}
              p={0}
              css="cursor: pointer;"
              height="auto"
              variant="tertiary"
              text={formatMoney(nextPossibleBid)}
              onClick={() => {
                setErrorMessage(null);
                setBidAmount(nextPossibleBid);
              }}
            />
          </Box>
        </>
      )}
    </>
  );
};

export default BidNowContent;
